import React, { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { ManageOfferingPluginsProps } from './types';
import { styles } from './styles';
import { Modal, Button, Input, LoadingBlock, Alert } from '@v2/components';
import { useActiveNonOfferingPlugins, useAddPluginToOffering } from '@v2/hooks';
import type { OfferingPlugin } from '@v2/types';

function formatReleaseDate(dateStr: string | undefined): string {
  if (!dateStr) return '—';
  try {
    const d = new Date(dateStr);
    return Number.isNaN(d.getTime()) ? dateStr : d.toLocaleDateString();
  } catch {
    return dateStr;
  }
}

function matchesSearch(plugin: OfferingPlugin, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const name = (plugin.name ?? '').toLowerCase();
  const description = (plugin.description ?? '').toLowerCase();
  return name.includes(q) || description.includes(q);
}

const ManageOfferingPlugins: React.FC<ManageOfferingPluginsProps> = ({
  offeringName,
  onClose,
  onCompleted,
}) => {
  const [searchQuery, setSearchQuery] = useState('');

  const { plugins, loading, error } = useActiveNonOfferingPlugins(offeringName);
  const { addPluginToOffering, loading: addLoading } = useAddPluginToOffering();

  const handleAdd = async (pluginId: string) => {
    if (!offeringName?.trim()) return;
    const result = await addPluginToOffering(offeringName.trim(), pluginId);
    if (result !== null) {
      toast.success('Plugin added to offering');
      onCompleted();
    } else {
      toast.error('Failed to add plugin to offering');
    }
  };

  const filteredPlugins = useMemo(() => {
    return plugins.filter(plugin => matchesSearch(plugin, searchQuery));
  }, [plugins, searchQuery]);

  const showList = !!offeringName?.trim();

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title="Add Plugins"
      size="lg"
      footer={
        <Button variant="primary" onClick={onClose}>
          Close
        </Button>
      }>
      {showList && (
        <div className={styles.searchContainer}>
          <Input
            label="Filter plugins"
            placeholder="Type to filter by name or description..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            autoComplete="off"
          />
        </div>
      )}

      <div className={styles.resultsContainer}>
        {!showList ? (
          <div className={styles.noPlugins}>Select an offering to manage plugins.</div>
        ) : loading ? (
          <LoadingBlock />
        ) : error ? (
          <Alert variant="error">{error}</Alert>
        ) : filteredPlugins.length === 0 ? (
          <div className={styles.emptyResults}>
            {plugins.length === 0 ? 'No plugins available to add.' : 'No matching plugins.'}
          </div>
        ) : (
          <ul className={styles.resultsList}>
            {filteredPlugins.map(plugin => (
              <li key={plugin.id} className={styles.resultItem}>
                <div className={styles.resultItemContent}>
                  <div className={styles.resultItemName}>{plugin.name}</div>
                  <div className={styles.resultItemMeta}>
                    <div className={styles.resultItemRow}>
                      <span className={styles.resultItemLabel}>Uid:</span>
                      <span className={styles.resultItemValue}>{plugin.id}</span>
                    </div>
                    <div className={styles.resultItemRow}>
                      <span className={styles.resultItemLabel}>Default Version:</span>
                      <span className={styles.resultItemValue}>
                        {plugin.defaultPluginVersion?.version ?? '—'}
                      </span>
                    </div>
                    <div className={styles.resultItemRow}>
                      <span className={styles.resultItemLabel}>Release Date:</span>
                      <span className={styles.resultItemValue}>
                        {formatReleaseDate(plugin.defaultPluginVersion?.releaseDate)}
                      </span>
                    </div>
                    <div className={styles.resultItemRow}>
                      <span className={styles.resultItemLabel}>Description:</span>
                      <span className={styles.resultItemValue}>
                        {plugin.defaultPluginVersion?.description ?? plugin.description ?? '—'}
                      </span>
                    </div>
                  </div>
                </div>
                <div className={styles.resultItemCta}>
                  <Button
                    variant="ghost-success"
                    size="sm"
                    onClick={() => handleAdd(plugin.id)}
                    disabled={addLoading}>
                    Add
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Modal>
  );
};

export default ManageOfferingPlugins;
